import { titleCaseSegment } from "../../strings.js";
import { getEntityTypeRouteLabelWithFallback } from "../entity-labels.js";
import { getCompiledRootFolderLabel } from "../root-folders.js";
import { findRootFolder, isProjectTreeFolderRoot, parseProjectPath } from "./parse.js";
function resolveCompiledFolderNodeLabel(input) {
    if (!input.structure) {
        return null;
    }
    if (!input.folderPath) {
        return getCompiledRootFolderLabel(input.structure, input.rootFolderSlug);
    }
    const rootFolder = findRootFolder(input.structure, input.rootFolderSlug);
    if (!rootFolder) {
        return null;
    }
    const segments = input.folderPath.split("/").filter(Boolean);
    if (segments.length === 0) {
        return getCompiledRootFolderLabel(input.structure, input.rootFolderSlug);
    }
    if (isProjectTreeFolderRoot({ structure: input.structure, rootFolderKey: rootFolder.key })) {
        if (!input.collectionSlug) {
            return titleCaseSegment(segments[0] ?? input.folderPath);
        }
        const rootNode = (input.structure.folderNodes ?? []).find((node) => node.kind === "root" && node.rootFolderKey === rootFolder.key);
        if (!rootNode) {
            return null;
        }
        return ((input.structure.folderNodes ?? []).find((node) => node.parentKey === rootNode.key &&
            node.kind === "collection" &&
            node.slug === input.collectionSlug)?.label ?? null);
    }
    const folderNodes = input.structure.folderNodes ?? [];
    const rootNode = folderNodes.find((node) => node.kind === "root" && node.rootFolderKey === rootFolder.key);
    if (!rootNode) {
        return null;
    }
    let parentKey = rootNode.key;
    let matchedNode = null;
    for (const segment of segments) {
        const childNode = folderNodes.find((node) => node.parentKey === parentKey &&
            node.slug === segment &&
            (node.kind === "folder" || node.kind === "collection")) ?? null;
        if (!childNode) {
            return null;
        }
        matchedNode = childNode;
        parentKey = childNode.key;
    }
    return matchedNode?.label ?? null;
}
function getProjectFolderRouteLabel(input) {
    const { rootFolderSlug, folderPath } = input.parsedPath.params;
    if (!rootFolderSlug || !input.structure) {
        return null;
    }
    const label = resolveCompiledFolderNodeLabel({
        structure: input.structure,
        rootFolderSlug,
        folderPath: folderPath ?? "",
        collectionSlug: input.parsedPath.params.collectionSlug,
    });
    if (!label) {
        return null;
    }
    return input.parsedPath.params.collectionSlug ? label : `Folder: ${label}`;
}
export function getProjectRouteLabel(input) {
    const parsedPath = parseProjectPath(input.path, {
        aliases: input.aliases,
        structure: input.structure,
    });
    switch (parsedPath.routeKey) {
        case "welcome":
            // The project root is the Project Home screen (LAT-1004); links to `/` name the screen.
            return "Project home";
        case "projectDocuments":
            return "Documents";
        case "aiAssistant":
            return "Claudia";
        case "aiAssistantNewChat":
            return "New Claudia chat";
        case "aiAssistantHistory":
            return "Claudia history";
        case "aiAssistantThread":
            return "Claudia conversation";
        case "specificationClarificationRequests":
            return "Clarification Requests";
        case "knowledge":
            return "Knowledge";
        case "specificationVersionSnapshots":
            return "Version Snapshots";
        case "specificationQualityGuard":
            return "Quality Guard";
        case "projectQualityGuardConfiguration":
            return "Quality Guard Configuration";
        case "entity": {
            const { entityTypeKey, entitySlug } = parsedPath.params;
            if (!entityTypeKey || !entitySlug) {
                return null;
            }
            return `${getEntityTypeRouteLabelWithFallback(input.structure, entityTypeKey)}: ${titleCaseSegment(entitySlug)}`;
        }
        case "folder":
            return getProjectFolderRouteLabel({
                parsedPath,
                structure: input.structure,
            });
        case "notFound":
            return null;
        default:
            return null;
    }
}
